import React from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  Box,
} from "@mui/material";
import { AttachMoney as MoneyIcon } from "@mui/icons-material";
import { amber } from "@mui/material/colors";

const CoinEconomySettings = ({ settings, onSettingChange }) => {
  const coinEconomySettings = settings.coinEconomy;

  const handleInputChange = (key, value, type = "int") => {
    let processedValue = value;

    if (type === "int") {
      processedValue = parseInt(value) || 0;
    } else if (type === "float") {
      processedValue = parseFloat(value) || 0;
    }

    console.log(`Updating ${key} to:`, processedValue);
    onSettingChange("coinEconomy", key, processedValue);
  };

  // ✅ Calculate preview values based on formula
  const getScreenshotPreview = () => {
    const baseRate = coinEconomySettings.screenshot_base_coin_rate || 1;
    const durations = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

    return durations.map((seconds) => ({
      seconds,
      coins: Math.ceil(seconds / 5) * baseRate,
    }));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={3}>
          <CardHeader
            avatar={<MoneyIcon sx={{ color: amber[500] }} />}
            title="Coin Economy Settings"
            subheader="Configure coin values and pricing for platform actions"
            sx={{ bgcolor: amber[50] }}
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tipping Coin Value"
                  type="number"
                  value={coinEconomySettings.tipping_coin_value || ""}
                  onChange={(e) =>
                    handleInputChange("tipping_coin_value", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Coins required for tipping"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Like XP Value"
                  type="number"
                  value={coinEconomySettings.like_coin_value || ""}
                  onChange={(e) =>
                    handleInputChange("like_coin_value", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Coins rewarded for message likes"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dislike XP Value"
                  type="number"
                  value={coinEconomySettings.dislike_coin_value || ""}
                  onChange={(e) =>
                    handleInputChange("dislike_coin_value", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Coins deducted for message dislikes"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Room Creation Base Fee"
                  type="number"
                  value={coinEconomySettings.room_creation_base_fee || ""}
                  onChange={(e) =>
                    handleInputChange("room_creation_base_fee", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Base cost to create a room"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Extend Room Time Fee (30 mins)"
                  type="number"
                  value={
                    coinEconomySettings.extend_room_time_coin_fee_for_thirty_mins ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      "extend_room_time_coin_fee_for_thirty_mins",
                      e.target.value
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Cost to extend room by 30 minutes"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Daily Bonus Base Amount"
                  type="number"
                  value={coinEconomySettings.daily_bonus_base_amount || ""}
                  onChange={(e) =>
                    handleInputChange("daily_bonus_base_amount", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Base daily bonus coins"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Message Interaction Cost"
                  type="number"
                  value={coinEconomySettings.message_interaction_cost || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "message_interaction_cost",
                      e.target.value
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">🪙</InputAdornment>
                    ),
                  }}
                  helperText="Cost per message interaction"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Coin to USD Conversion Rate"
                  type="number"
                  step="0.01"
                  value={coinEconomySettings.coin_to_usd_conversion_rate || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "coin_to_usd_conversion_rate",
                      e.target.value,
                      "float"
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  helperText="USD value of 1 coin"
                />
              </Grid>

              {/* ✅ NEW: Screenshot Base Rate with Preview */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                  Screenshot Payment Pricing
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Screenshot Base Rate"
                      type="number"
                      value={
                        coinEconomySettings.screenshot_base_coin_rate || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "screenshot_base_coin_rate",
                          e.target.value
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">🪙</InputAdornment>
                        ),
                      }}
                      helperText="Base coin rate (1 coin per 5 seconds)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Alert
                      severity="info"
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2">
                        Formula: <code>Math.ceil(seconds / 5) × base_rate</code>
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>

                {/* Preview Table */}
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    💡 Preview (based on current base rate):
                  </Typography>
                  <Grid container spacing={1}>
                    {getScreenshotPreview().map(({ seconds, coins }) => (
                      <Grid item xs={3} sm={2} md={1} key={seconds}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: "white",
                            borderRadius: 1,
                            textAlign: "center",
                            border: "1px solid",
                            borderColor: "grey.300",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {seconds}s
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            🪙 {coins}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CoinEconomySettings;
